import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getBanners, getProducts, getReviews, getVideo, getCategories } from "../utils/api";
import BannerCarousel from "../components/BannerCarousel";
import ReviewsCarousel from "../components/ReviewsCarousel";
import ProductCard from "../components/ProductCard";
import "./HomePage.css";
import giftBox from "../assets/images/giftBox.png";
import questionImg from "../assets/images/questionImg.png";
import cargoImg from "../assets/images/cargoImg.png";
import factoryImg from "../assets/images/factoryImg.png";
import SEO from "../components/SEO";

export default function HomePage() {
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [video, setVideo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [b, p, r, v, c] = await Promise.allSettled([
          getBanners(),
          getProducts(),
          getReviews(),
          getVideo(),
          getCategories(),
        ]);
        if (b.status === "fulfilled") setBanners(b.value.data.data || []);
        if (p.status === "fulfilled") setProducts((p.value.data.data || []).slice(0, 6));
        if (r.status === "fulfilled") setReviews(r.value.data.data || []);
        if (v.status === "fulfilled") setVideo(v.value.data.data);
        if (c.status === "fulfilled") setCategories(c.value.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const getYoutubeEmbed = (url) => {
    if (!url) return null;
    const match = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=0&rel=0` : url;
  };

  const goToCategory = (cat) => {
    navigate(`/catalog?categoryId=${cat.id}&categoryName=${encodeURIComponent(cat.name)}`);
  };

  // Grid class based on count for layout tweaks
  const gridClass = `categories-grid${categories.length === 8 ? " cols-8" : ""}`;

  return (
    <div className="home-page">
      <SEO
        title="Декор та фотошпалери для дитячої кімнати"
        description="Zori.place — фотошпалери, наліпки та дерев'яний декор ручної роботи з 2019 року. Виготовлення 1–3 дні, доставка по всій Україні."
        url="https://zori.place/"
      />
      {/* Hero Banner */}
      <BannerCarousel banners={banners} />

      {/* Categories Grid */}
      {(loading || categories.length > 0) && (
        <section className="home-categories">
          <div className="container">
            <h2 className="home-section-label">Каталог</h2>
            {loading ? (
              <div className="spinner" />
            ) : (
              <div className={gridClass}>
                {categories.map((cat) => {
                  const imgUrl = cat.image
                    ? cat.image.startsWith("http")
                      ? cat.image
                      : `${cat.image}`
                    : null;
                  return (
                    <button
                      key={cat.id}
                      className="category-card"
                      onClick={() => {
                        if (cat.name === "Індивідуальне замовлення") {
                          navigate("/individual-order");
                          return;
                        }
                        goToCategory(cat);
                      }}
                    >
                      <div className="category-card-img">
                        {imgUrl ? (
                          <img src={imgUrl} alt={cat.name} />
                        ) : (
                          <span className="category-card-emoji">🏷️</span>
                        )}
                      </div>
                      <span className="category-card-name">{cat.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Products Section */}
      <section className="home-products">
        <div className="container">
          <h2 className="home-section-label">Популярні товари</h2>
          {loading ? (
            <div className="spinner" />
          ) : products.length === 0 ? (
            <p className="empty-state">Товари ще не додані. Зайдіть пізніше!</p>
          ) : (
            <div className="products-grid">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Promo Strip */}
      <section className="home-promo-strip">
        <div className="container">
          <div className="promo-strip-block">
            <Link to="/individual-order" className="promo-card promo-card--sage">
              <div className="promo-card-icon">
                <img src={factoryImg} alt="індивідуальне замовлення" />
              </div>
              <div className="promo-card-body">
                <h4>Індивідуальне замовлення</h4>
                <p>Унікальний дизайн під ваш простір</p>
                <span className="promo-card-link one">Замовити</span>
              </div>
            </Link>

            <Link to="/certificate" className="promo-card promo-card--pink">
              <div className="promo-card-icon">
                <img src={giftBox} alt="подарунок іконка" />
              </div>
              <div className="promo-card-body">
                <h4>Подарунковий сертифікат</h4>
                <p>Ідеальний подарунок для близьких на будь-яку суму</p>
                <span className="promo-card-link two">Замовити сертифікат</span>
              </div>
            </Link>

            <Link to="/delivery" className="promo-card promo-card--cream">
              <div className="promo-card-icon">
                <img src={cargoImg} alt="доставка іконка" />
              </div>
              <div className="promo-card-body">
                <h4>Безкоштовна доставка</h4>
                <p>При замовленні від 3500 грн доставка безкоштовна</p>
                <span className="promo-card-link three">Умови доставки</span>
              </div>
            </Link>

            <Link to="/baby-package" className="promo-card promo-card--gold">
              <div className="promo-card-icon">
                <img src={questionImg} alt="питання іконка" />
              </div>
              <div className="promo-card-body">
                <h4>Відповіді на всі питання</h4>
                <p>Зв'яжіться з нами перед замовленням</p>
                <span className="promo-card-link four">Дізнатись</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* About + Video/Photo Section */}
      <section className="home-about-video">
        <div className="container">
          <div className="about-video-grid">
            <div className="home-about-text">
              <p className="section-tag">Про нас</p>
              <h2 className="home-about-title">
                Zori.place <em>— це не просто декор, це атмосфера дитинства</em>
              </h2>
              <p>
                З 2019 року ми створюємо фотошпалери, наліпки, дерев'яний декор і постери, які
                перетворюють кімнату на затишний і казковий простір.
              </p>

              <p>
                <strong>Саме тому ви отримуєте:</strong>
              </p>

              <ul>
                <li>швидке виготовлення (1–3 дні) без довгого очікування</li>
                <li>найвищу якість, яку видно з першого погляду</li>
                <li>унікальні рішення, які ідеально підходять саме під вашу кімнату</li>
              </ul>

              <p>
                Кожен наш виріб — це не просто декор, а частина атмосфери, де дитина росте,
                грається, мріє та відчуває себе щасливою.
              </p>

              <p>
                <strong>Zori.place</strong> — коли кімната стає особливим місцем.
              </p>

              <div className="home-stats">
                <div className="home-stat">
                  <span className="home-stat-n">500+</span>
                  <span>товарів</span>
                </div>
                <div className="home-stat">
                  <span className="home-stat-n">5 000+</span>
                  <span>задоволених клієнтів</span>
                </div>
                <div className="home-stat">
                  <span className="home-stat-n">З 2019</span>
                  <span>Зроблено з любов'ю</span>
                </div>
              </div>
            </div>

            {/* Right column: YouTube video or photo placeholder */}
            {video && video.url ? (
              <div className="video-wrapper">
                <iframe
                  src={getYoutubeEmbed(video.url)}
                  title="Відео про компанію"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="home-about-visual">
                <div className="about-visual-inner">
                  {/* Replace with <img src={yourPhoto} alt="Про нас" /> when photo is ready */}
                  <span>🎨</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="home-reviews">
          <div className="container">
            <h2 className="home-section-label center">Відгуки клієнтів</h2>
            <ReviewsCarousel reviews={reviews} />
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section className="home-cta">
        <div className="container">
          <div className="cta-inner">
            <h2>Залишились питання? Зв'яжіться з нами</h2>
            <p>Ми завжди раді допомогти та відповісти на усі ваші запитання</p>
            <Link to="/contact" className="btn-primary cta-btn">
              ЗВ'ЯЗАТИСЬ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
